import React, { useEffect, useRef } from 'react';
import ReactDOM, { createPortal } from 'react-dom';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { motion, AnimatePresence } from 'framer-motion';
import { usePortal } from '../../hooks';

const drawerWrapVariants = {
  closed: {
    opacity: 0,
  },
  open: {
    opacity: 1,
  },
};

const drawerInnerVariants = {
  closed: (position) => ({
    opacity: 0,
    x: position == 'left' ? '-100%' : '100%',
  }),
  open: {
    opacity: 1,
    x: 0,
    transition: {
      type: 'spring',
      damping: 40,
      stiffness: 200,
    },
  },
};

export const ReactDrawer = ({ parentNode, showDrawer, setShowDrawer, children, className, position, id }) => {
  const drawerInnerEl = useRef(null);
  const target = usePortal('modals-container');

  // Bind Esc key to close the modal
  useEffect(() => {
    if (showDrawer) {
      function handleKeyUp(event) {
        switch (event.key) {
          case 'Escape':
            setShowDrawer(false);
            break;
        }
      }
      window.addEventListener('keyup', handleKeyUp);
      return () => window.removeEventListener('keyup', handleKeyUp);
    }
  }, [showDrawer]);

  useEffect(() => {
    if (drawerInnerEl.current) {
      if (showDrawer) {
        drawerInnerEl.current.focus();
        disableBodyScroll(drawerInnerEl.current);
      } else {
        enableBodyScroll(drawerInnerEl.current);
      }
    }
    return () => {
      enableBodyScroll(drawerInnerEl.current);
    };
  }, [showDrawer]);

  return createPortal(
    <AnimatePresence>
      {showDrawer && (
        <motion.div
          initial={'closed'}
          custom={position}
          animate={'open'}
          exit={'closed'}
          variants={drawerWrapVariants}
          className={`Drawer ${className}`}
        >
          <motion.div
            custom={position}
            initial={'closed'}
            animate={'open'}
            exit={'closed'}
            variants={drawerInnerVariants}
            className="Drawer__inner"
            ref={drawerInnerEl}
            id={id}
            tabIndex="-1"
            style={position == 'left' ? { left: 0 } : { right: 0 }}
          >
            {children}
          </motion.div>
          <span className="backdrop" onClick={() => setShowDrawer(false)}></span>
        </motion.div>
      )}
    </AnimatePresence>,
    target
  );
};
